import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { coursesAPI } from "../../services/api";
import type { Course } from "../../types/course";

interface CoursesState {
  courses: Course[];
  page: number;
  hasMore: boolean;
  status: "idle" | "loading" | "success" | "error";
  error: string | null;
  // Server-side search state
  searchQuery: string;
  searchResults: Course[];
  searchPage: number;
  searchHasMore: boolean;
  searchStatus: "idle" | "loading" | "success" | "error";
}

const initialState: CoursesState = {
  courses: [],
  page: 1,
  hasMore: true,
  status: "idle",
  error: null,
  searchQuery: "",
  searchResults: [],
  searchPage: 1,
  searchHasMore: true,
  searchStatus: "idle",
};

// Async thunk to fetch courses with pagination
export const fetchCourses = createAsyncThunk(
  "courses/fetchCourses",
  async ({ page, isPublic }: { page: number; isPublic?: boolean }) => {
    const response = await coursesAPI.getCourses({ page, isPublic });
    return response.data;
  }
);

// Async thunk to search courses server-side
export const searchCourses = createAsyncThunk(
  "courses/searchCourses",
  async ({ query, page, isPublic }: { query: string; page: number; isPublic?: boolean }) => {
    const response = await coursesAPI.getCourses({ page, search: query, isPublic });
    return response.data;
  }
);

// Async thunk to fetch single course by ID
export const fetchCourse = createAsyncThunk("courses/fetchCourse", async (id: string) => {
  const response = await coursesAPI.getCourse(id);
  return response.data;
});

const coursesSlice = createSlice({
  name: "courses",
  initialState,
  reducers: {
    resetCourses: (state) => {
      state.courses = [];
      state.page = 1;
      state.hasMore = true;
      state.status = "idle";
      state.error = null;
    },
    resetSearch: (state) => {
      state.searchQuery = "";
      state.searchResults = [];
      state.searchPage = 1;
      state.searchHasMore = true;
      state.searchStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch courses
      .addCase(fetchCourses.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchCourses.fulfilled, (state, action) => {
        state.status = "success";
        const courses = Array.isArray(action.payload) ? action.payload : [];
        const fetchedPage = action.meta.arg.page;

        // If fetching page 1, replace all courses, otherwise append
        if (fetchedPage === 1) {
          state.courses = courses;
          state.page = 2; // Next page to fetch is 2
        } else {
          // Filter out duplicates by _id
          const existingIds = new Set(state.courses.map((c) => c._id));
          const newCourses = courses.filter((c) => !existingIds.has(c._id));
          state.courses = [...state.courses, ...newCourses];
          state.page = fetchedPage + 1; // Next page to fetch
        }

        // Update hasMore flag
        state.hasMore = courses.length >= 10; // Assuming page_length is 10
      })
      .addCase(fetchCourses.rejected, (state, action) => {
        state.status = "error";
        state.error = action.error.message || "Failed to fetch courses";
        state.hasMore = false;
      })
      // Search courses
      .addCase(searchCourses.pending, (state, action) => {
        state.searchStatus = "loading";
        state.searchQuery = action.meta.arg.query;
      })
      .addCase(searchCourses.fulfilled, (state, action) => {
        // Ignore stale responses — a newer search was dispatched after this one
        if (action.meta.arg.query !== state.searchQuery) return;

        state.searchStatus = "success";
        const courses = Array.isArray(action.payload) ? action.payload : [];
        const fetchedPage = action.meta.arg.page;

        if (fetchedPage === 1) {
          state.searchResults = courses;
          state.searchPage = 2;
        } else {
          const existingIds = new Set(state.searchResults.map((c) => c._id));
          const newCourses = courses.filter((c) => !existingIds.has(c._id));
          state.searchResults = [...state.searchResults, ...newCourses];
          state.searchPage = fetchedPage + 1;
        }

        state.searchHasMore = courses.length >= 10;
      })
      .addCase(searchCourses.rejected, (state, action) => {
        // Ignore stale rejections — a newer search was dispatched after this one
        if (action.meta.arg.query !== state.searchQuery) return;
        state.searchStatus = "error";
        state.searchHasMore = false;
      });
  },
});

export const { resetCourses, resetSearch } = coursesSlice.actions;
export default coursesSlice.reducer;
